<template>
    <v-form ref="form" v-model="valid" data-cy="guest-category-form" class="pb-4">
        <v-row
            v-if="
                (isHotelDescriptionMode || (serviceType === contractServiceType.ownHotel && contractId !== 0)) &&
                useIsLoadingContract
            "
            dense
        >
            <v-sheet class="full-width d-flex justify-center py-2">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <v-row dense class="mb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row v-if="contract && !isHotelDescriptionMode" dense>
            <v-col cols="12" md="2" order="2" order-md="1">
                <span class="text-h6 text--secondary">
                    {{ $t('general_info') }}
                </span>
            </v-col>
            <v-col cols="12" md="8" order="3" order-md="2">
                <TextFieldLocale
                    v-model="contract.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                    :rules="[requiredRule('name'), maxLength(255)]"
                    class="mb-2"
                />

                <v-tooltip :disabled="!isSupplierDropdownDisabled" location="top">
                    <template v-slot:activator="{props: tooltipProps}">
                        <div v-bind="tooltipProps" class="mb-2">
                            <v-text-field
                                v-if="isSupplierDropdownDisabled"
                                :model-value="selectedSupplierName"
                                :label="`${$t('filterTitles.supplierName')}*`"
                                variant="outlined"
                                density="compact"
                                disabled
                                class="disabled-supplier-text-field"
                            />
                            <v-autocomplete
                                v-else
                                v-model="contract.supplierId"
                                :disabled="serviceType === contractServiceType.customComplexTour"
                                :rules="[requiredRule('filterTitles.supplierName')]"
                                variant="outlined"
                                density="compact"
                                color="primary"
                                data-cy="supplier"
                                :items="supplierStore.internalSuppliers"
                                item-title="name"
                                item-value="id"
                                :clearable="serviceType !== contractServiceType.customComplexTour"
                                :label="`${$t('filterTitles.supplierName')}*`"
                                :loading="isCheckingOrders"
                            />
                        </div>
                    </template>
                    <span>{{ $t('tooltip.supplier_disabled_has_orders') }}</span>
                </v-tooltip>
                <p
                    v-if="
                        !isSupplierDropdownDisabled && serviceType === contractServiceType.ownHotel && contractId === 0
                    "
                    class="text-grey text-subtitle-2 font-weight-regular pb-6"
                >
                    {{ $t('hotel_creation_note') }}
                </p>
                <v-select
                    v-if="!contract.id"
                    v-model="contract.transactionsCurrency"
                    :items="useCurrency"
                    :label="`${$t('default_currency')}*`"
                    :rules="[requiredRule('default_currency')]"
                    density="compact"
                    data-cy="transactionsCurrency"
                    max-width="200"
                />
            </v-col>
            <v-col cols="12" md="2" class="pl-4" order="1" order-md="4">
                <v-row dense>
                    <div v-if="contract.id" class="text-medium-emphasis ml-auto px-4">
                        {{ $t('id') }}: {{ contract.id }}
                    </div>
                </v-row>
                <v-row dense class="px-4">
                    <v-switch
                        v-model="contract.active"
                        color="primary"
                        :label="contract.active ? $t('active') : $t('inactive')"
                        data-cy="status"
                        hide-details
                        density="compact"
                        class="ml-auto pb-2"
                        :disabled="!authStore?.isTO1 && !contract.active"
                    />
                </v-row>
                <v-select
                    v-if="useIsShowPublishing"
                    v-model="contract.stage"
                    :items="useContractStage"
                    :label="`${$t('stage')}*`"
                    :rules="[requiredRule('stage')]"
                    density="compact"
                    data-cy="stage"
                    max-width="200"
                />
                <v-checkbox
                    v-if="useIsShowRecommended"
                    v-model="contract.recommended"
                    hide-details
                    :label="$t('recommended')"
                    density="compact"
                    data-cy="recommended"
                />
            </v-col>
        </v-row>
        <InfoContractCharterForm v-if="serviceType === contractServiceType.ownCharter && contract" v-model="contract" />
        <InfoContractExcursionForm
            v-if="serviceType === contractServiceType.ownExcursion && contract"
            v-model="contract"
            :edit-mode="!!contractId"
        />
        <InfoContractEventForm
            v-if="serviceType === contractServiceType.concert && contract"
            v-model="contract"
            :edit-mode="!!contractId"
        />
        <InfoContractTransferForm
            v-if="serviceType === contractServiceType.ownTransfer && contract"
            v-model="contract"
            :edit-mode="!!contractId"
        />
        <InfoContractCarHireForm
            v-if="serviceType === contractServiceType.ownCarRent && contract"
            v-model="contract"
            :supplier-id="contract?.supplierId"
        />
        <InfoContractCruiseForm
            v-if="
                (serviceType === contractServiceType.ownCruise || serviceType === contractServiceType.tour) && contract
            "
            v-model="contract"
            :service-type="serviceType"
            @validate="form.validate()"
        />
        <InfoContractExtraServiceForm
            v-if="serviceType === contractServiceType.ownExtraService && contract"
            v-model="contract"
        />
        <InfoContractPackageTourForm
            v-if="serviceType === contractServiceType.customComplexTour && contract"
            v-model="contract"
        />
        <InfoContractInsuranceForm
            v-if="serviceType === contractServiceType.ownInsurance && contract"
            ref="insuranceFormRef"
            v-model="contract"
        />
        <InfoContractVisaForm
            v-if="serviceType === contractServiceType.ownVisa && contract"
            ref="visaFormRef"
            v-model="contract"
        />
        <v-row v-if="contract && authStore?.isTO1 && !isHotelDescriptionMode" dense class="mb-4">
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('internal_note', 2) }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <v-textarea
                    v-model="contract.internalDescription"
                    variant="outlined"
                    color="primary"
                    data-cy="comments"
                />
            </v-col>
        </v-row>
        <InfoContractHotelForm
            v-if="(serviceType === contractServiceType.ownHotel || isHotelDescriptionMode) && extraFormData"
            ref="hotelFormRef"
            v-model="extraFormData"
            :active-lang-code="activeLangCode"
            :associate-with-hotel="associateWithHotel"
            @update-contract="loadData"
            @cancel="emit('cancel')"
            @add-associate-hotel-id="e => (contract.hotelContentId = e)"
        />
        <v-row v-if="hotelId" dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('channel_managers') }}
                </span>
            </v-col>
            <v-col cols="12" md="8">
                <ChannelManagersConfig
                    v-model="channelManagersConfigItems"
                    :is-loading="isLoadingChannelManagersConfig"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn
                v-if="!isHotelDescriptionMode || app.$conf.public.account.allowToEditExternalHotelDescription"
                class="save-btn"
                data-cy="save"
                :disabled="isLoading || useIsLoadingContract || !valid"
                @click="checkValidation"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading || useIsLoadingContract"
                variant="text"
                @click="cancel"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {maxLength, requiredRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import InfoContractCharterForm from '~/components/products/information/InfoContractCharterForm.vue'
    import {contractServiceType} from '~/utils/types/types'
    import {
        initCarHireContract,
        initCharterContract,
        initCruiseContract,
        initEventContract,
        initExcursionContract,
        initExtraServiceContract,
        initHotelContract as coreInitHotelContract,
        initHotelInfo as coreInitHotelInfo,
        initInsuranceContract,
        initPackageTourContract,
        initTourContract,
        initTransferContract,
        initVisaContract,
    } from '~/utils/types/account/products/helpers'
    import InfoContractExcursionForm from '~/components/products/information/InfoContractExcursionForm.vue'
    import InfoContractEventForm from '~/components/products/information/InfoContractEventForm.vue'
    import InfoContractTransferForm from '~/components/products/information/InfoContractTransferForm.vue'
    import InfoContractCarHireForm from '~/components/products/information/InfoContractCarHireForm.vue'
    import InfoContractCruiseForm from '~/components/products/information/InfoContractCruiseForm.vue'
    import InfoContractExtraServiceForm from '~/components/products/information/InfoContractExtraServiceForm.vue'
    import InfoContractPackageTourForm from '~/components/products/information/InfoContractPackageTourForm.vue'
    import {clone} from '@/utils/helpers'
    import InfoContractHotelForm from '~src/components/products/information/InfoContractHotelForm.vue'
    import {useHotelInfo} from '~/composables/form-helpers'
    import InfoContractInsuranceForm from '~/components/products/information/InfoContractInsuranceForm.vue'
    import InfoContractVisaForm from '~/components/products/information/InfoContractVisaForm.vue'
    import {useHotelOrdersCheck} from '~src/composables/useHotelOrdersCheck'
    import ChannelManagersConfig from './ChannelManagersConfig.vue'

    // ZSH-specific override for initHotelInfo to fix validation and set proper active state
    const initHotelInfo = () => ({
        ...coreInitHotelInfo(),
        supplierId: null, // Override to null so validation works properly
        active: authStore?.isTO1 ?? true, // Set initial active state based on user role, default to true if authStore not ready
    })

    // ZSH-specific override for initHotelContract to set proper active state and stage
    const initHotelContract = () => ({
        ...coreInitHotelContract(),
        active: authStore?.isTO1 ?? true, // Set initial active state based on user role, default to true if authStore not ready
        stage: 'DRAFT', // Always set stage to DRAFT for new hotels (both TO1 and non-TO1 users)
    })

    const props = defineProps<{
        contractId?: number | null
        serviceType: string
        associateWithHotel?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()
    const router = useRouter()

    const valid = ref<boolean>(false)
    const form = ref()
    const insuranceFormRef = ref()
    const hotelFormRef = ref()
    const visaFormRef = ref()
    const isLoading = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const contract = ref()
    const extraFormData = ref(initHotelInfo())
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const isShowValidationError = ref<boolean>(false)
    provide('contractId', props.contractId)
    provide('isShowValidationError', isShowValidationError)

    // Hotel orders check composable
    const {isCheckingOrders, hasOrders, checkHotelOrders, resetOrdersCheck} = useHotelOrdersCheck()

    const appMethod = ref(null)
    const methods = {
        ownCharter: 'ownCharterContracts',
        ownExcursion: 'ownExcursionContracts',
        concert: 'ownEventContracts',
        ownTransfer: 'ownTransferContracts',
        ownCarRent: 'ownCarRentContracts',
        tour: 'ownTourContracts',
        ownCruise: 'ownCruiseContracts',
        ownVisa: 'ownVisaContracts',
        ownExtraService: 'ownExtraServiceContracts',
        ownInsurance: 'ownInsuranceContracts',
        ownHotel: 'ownAccommodationContracts',
        customComplexTour: 'ownPackageContracts',
    }

    if (props.serviceType === contractServiceType.ownHotel) {
        watch(useHotelInfo, async () => {
            extraFormData.value = clone(useHotelInfo.value)

            if (!props.contractId && useIsShowPublishing.value === false) {
                // For non-TO1 users, set active to false and disable the toggle
                // For TO1 users, keep the current behavior (active = true)
                extraFormData.value.active = authStore?.isTO1 ?? true
            }
        })
    }

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        appMethod.value = methods[props.serviceType]
        if (props.contractId === 0) {
            contract.value = {...useBaseFormData.value, transactionsCurrency: authStore.systemCurrency}
            if (props.serviceType === contractServiceType.customComplexTour) {
                contract.value.supplierId = authStore.company.id
            }
        }
        if (appMethod.value && props.contractId) {
            if (!supplierStore.fullContract?.id) {
                await loadData()
            } else {
                contract.value = prepareContractData(clone(supplierStore.fullContract))

                // Also check for orders when using existing contract
                if (props.serviceType === contractServiceType.ownHotel && props.contractId && props.contractId !== 0) {
                    await checkHotelOrders(props.contractId)
                }
            }
        }
        if ((props.serviceType === contractServiceType.ownHotel && props.contractId) || isHotelDescriptionMode) {
            extraFormData.value = clone(useHotelInfo.value)
        }

        // For new hotel creation, set initial active state based on user role
        if (props.serviceType === contractServiceType.ownHotel && props.contractId === 0) {
            extraFormData.value.active = authStore?.isTO1 ?? true
        }
    })

    const useIsLoadingContract = computed(() => {
        return supplierStore.isLoading
    })

    const useIsShowPublishing = computed(() => {
        return runtimeStore.getCRMOption('asp.option.show.product.publishing') && (authStore?.isTO1 ?? false)
    })

    // Computed property to determine if supplier dropdown should be disabled
    const isSupplierDropdownDisabled = computed(() => {
        // Only check for hotel contracts
        if (props.serviceType !== contractServiceType.ownHotel) return false

        // Don't disable during creation mode
        if (props.contractId === 0) return false

        // Don't disable if we're still checking for orders
        if (isCheckingOrders.value) return false

        // Disable if hotel has existing orders
        const shouldDisable = hasOrders.value

        return shouldDisable
    })

    const selectedSupplierName = computed(() => {
        if (contract.value?.supplierId && supplierStore.internalSuppliers?.length) {
            const supplier = supplierStore.internalSuppliers.find(s => s.id === contract.value.supplierId)
            return supplier?.name || ''
        }
        return ''
    })

    async function loadData(): Promise<void> {
        await supplierStore.loadFullContract()
        contract.value = prepareContractData(clone(supplierStore.fullContract))

        // Check for existing orders when contract is loaded (for hotel contracts only)
        if (props.serviceType === contractServiceType.ownHotel && props.contractId && props.contractId !== 0) {
            await checkHotelOrders(props.contractId)
        }
    }

    const useBaseFormData = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return initHotelContract()
            case contractServiceType.ownCharter:
                return initCharterContract()
            case contractServiceType.ownExcursion:
                return initExcursionContract()
            case contractServiceType.concert:
                return initEventContract()
            case contractServiceType.ownTransfer:
                return initTransferContract()
            case contractServiceType.ownCarRent:
                return initCarHireContract()
            case contractServiceType.tour:
                return initTourContract()
            case contractServiceType.ownCruise:
                return initCruiseContract()
            case contractServiceType.ownExtraService:
                return initExtraServiceContract()
            case contractServiceType.customComplexTour:
                return initPackageTourContract()
            case contractServiceType.ownInsurance:
                return initInsuranceContract()
            case contractServiceType.ownVisa:
                return initVisaContract()
            default:
                return {name: {}, productName: null, supplierId: null, active: true}
        }
    })

    const prepareContractData = contract => {
        const {airlineCode, departureCharterLocation, destinationCharterLocation, productVariant} = contract
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                delete contract.address
                delete contract.cityName
                delete contract.cityId
                delete contract.countryName
                delete contract.countryId
                delete contract.ratingStars
                delete contract.category

                return Object.assign(initHotelContract(), contract)
            case contractServiceType.ownCharter:
                delete contract.airlineName
                return Object.assign(contract, {
                    airlineCode: airlineCode || null,
                    departureCharterLocation: {
                        airportCode: departureCharterLocation?.airportCode || null,
                    },
                    destinationCharterLocation: {
                        airportCode: destinationCharterLocation?.airportCode || null,
                    },
                })
            case contractServiceType.ownExcursion:
                return Object.assign(contract, {
                    variantId: productVariant?.id ? productVariant?.id : null,
                })
            case contractServiceType.concert:
                return Object.assign(contract, {
                    variantId: productVariant?.id ? productVariant?.id : null,
                })
            case contractServiceType.ownTransfer:
                return Object.assign(contract, {
                    variantId: productVariant?.id ? productVariant?.id : null,
                })
            case contractServiceType.ownCruise:
                return Object.assign(contract, {
                    departureLocations: contract.departureLocations?.length
                        ? contract.departureLocations
                        : [{countryId: null, cityId: null}],
                    destinationLocations: contract.destinationLocations?.length ? contract.destinationLocations : [],
                })
            case contractServiceType.tour:
                return Object.assign(contract, {
                    destinationLocations: contract.destinationLocations?.length ? contract.destinationLocations : [],
                })
            case contractServiceType.ownExtraService:
                return Object.assign(contract, {
                    locations: contract.locations?.length ? contract.locations : [],
                })
            case contractServiceType.customComplexTour:
                return Object.assign(contract, {
                    destinationLocations: contract.destinationLocations?.length ? contract.destinationLocations : [],
                    departureLocations: contract.departureLocations?.length ? contract.departureLocations : [],
                    tags: contract.tags,
                })
            default:
                return contract
        }
    }

    const checkValidation = () => {
        if (
            props.serviceType === contractServiceType.ownExcursion ||
            props.serviceType === contractServiceType.ownTransfer
        ) {
            const {departureLocations, destinationLocations} = contract.value
            if (!departureLocations?.length || !destinationLocations?.length) {
                isShowValidationError.value = true
            }
            if (departureLocations?.length && destinationLocations?.length) {
                isShowValidationError.value = false
            }
        }

        if (props.serviceType === contractServiceType.tour || props.serviceType === contractServiceType.ownCruise) {
            const {destinationLocations} = contract.value
            isShowValidationError.value = destinationLocations?.length === 0
        }

        if (!isShowValidationError.value) save()
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(contract.value)
            clean(data)
            if (data?.departureLocations?.length) {
                data.departureLocations = data.departureLocations.map(item => {
                    delete item.id
                    return item
                })
                if (props.serviceType === contractServiceType.ownCruise) {
                    const country = data.departureLocations[0]?.countryId
                    const city = data.departureLocations[0]?.cityId
                    if (!country && !city) {
                        data.departureLocations = []
                    }
                }
            }
            if (data?.destinationLocations?.length) {
                data.destinationLocations = data.destinationLocations.map(item => {
                    delete item.id
                    return item
                })
            }
            if (data?.locations?.length) {
                data.locations = data.locations.map(item => {
                    delete item.id
                    return item
                })
            }
            if (props.serviceType === contractServiceType.ownHotel) {
                const saveHotelInfoRs = await saveHotelInfo()
                if (saveHotelInfoRs?.hotelId) {
                    await saveChannelManagersConfig(saveHotelInfoRs.hotelId)
                }

                if (props.contractId === 0 && !isHotelDescriptionMode && props.associateWithHotel === false) {
                    data.hotelContentId = saveHotelInfoRs.hotelId
                }
                if (hotelFormRef.value?.hotelLogo?.toDelete) {
                    await removeHotelLogo()
                }
                if (hotelFormRef.value?.logo) {
                    await saveHotelLogo()
                }
            }
            if (props.contractId) {
                const {
                    ownProductContracts: [_contract],
                } = await app.$api[appMethod.value].put(data)
                supplierStore.fullContract = _contract

                if (props.serviceType === contractServiceType.ownInsurance) {
                    await insuranceFormRef.value.uploadFile(_contract.id)
                }
                if (props.serviceType === contractServiceType.ownVisa) {
                    await visaFormRef.value.uploadFile(_contract.id)
                }
            } else if (!isHotelDescriptionMode) {
                const {
                    ownProductContracts: [_contract],
                } = await app.$api[appMethod.value].post(data)

                if (props.serviceType === contractServiceType.ownInsurance) {
                    await insuranceFormRef.value.uploadFile(_contract.id)
                }
                if (props.serviceType === contractServiceType.ownVisa) {
                    await visaFormRef.value.uploadFile(_contract.id)
                }
                await router.push({name: 'supplierContract', params: {contractId: _contract.id}})
            }

            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(
                `${t('error_message.save_error')}: ${e.msg || e.errors?.[0]?.message || t('error_message.unknown_error')}`
            )
        } finally {
            isLoading.value = false
        }
    }

    const removeHotelLogo = async () => {
        const logoId = hotelFormRef.value?.hotelLogo?.id
        if (logoId) {
            try {
                await app.$api.hotelDescriptionImages.remove(supplierStore.hotelInfo?.hotelId, logoId)
                hotelFormRef.value.hotelLogo = null
            } catch {
                app.$toast.error(t('error_message.save_error'))
            }
        }
    }

    const saveHotelLogo = async () => {
        const encodedData = hotelFormRef.value.logo.split('base64,')[1]
        const rq = {
            imageType: 'LOGO',
            title: {},
            imageData: encodedData,
            mainPhoto: false,
            orderNumber: 1,
        }
        try {
            await app.$api.hotelDescriptionImages.post(supplierStore.hotelInfo?.hotelId, rq)
        } catch {
            app.$toast.error(t('error_message.save_error'))
        }
    }

    const saveHotelInfo = async () => {
        const data = clone(extraFormData.value)

        // Synchronize hotel info active status with contract active status
        if (contract.value && contract.value.hasOwnProperty('active')) {
            data.active = contract.value.active
        }

        clean(data)
        try {
            const rs = data.hotelId
                ? await app.$api.ownHotelInfo.put(data.hotelId, data)
                : await app.$api.ownHotelInfo.post(data)
            await supplierStore.loadHotelInfo(rs.hotelId)

            return rs
        } catch (e) {
            // Toasting is now handled by the main save() function's catch block.
            throw e // Re-throw the original error to be caught by save().
        }
    }

    const cancel = (): void => {
        if (isHotelDescriptionMode) router.push({name: 'supplierProducts'})
        if (props.contractId)
            router.push({
                name: 'supplierProducts',
                hash: `#${
                    props.serviceType === 'ownExcursion' || props.serviceType === 'concert'
                        ? 'activities'
                        : props.serviceType
                }`,
            })
        if (props.contractId === 0 && !isHotelDescriptionMode) emit('cancel')
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const useIsShowRecommended = computed(() => {
        const availableForRecommending: string[] = [contractServiceType.ownExcursion, contractServiceType.concert]
        return availableForRecommending.includes(props.serviceType)
    })

    const useIsShowVisibleInReservation = computed(() => {
        const available: string[] = [
            contractServiceType.ownHotel,
            contractServiceType.ownCharter,
            contractServiceType.ownExcursion,
            contractServiceType.concert,
            contractServiceType.ownCruise,
            contractServiceType.ownTransfer,
            contractServiceType.ownCarRent,
            contractServiceType.ownExtraService,
            contractServiceType.ownInsurance,
            contractServiceType.ownVisa,
        ]
        return available.includes(props.serviceType)
    })

    const useIsShowNameForSupplier = computed(() => {
        const excludedProducts: string[] = [
            contractServiceType.customComplexTour,
            contractServiceType.tour,
            contractServiceType.ownInsurance,
            contractServiceType.ownVisa,
        ]
        return !excludedProducts.includes(props.serviceType)
    })

    const useContractStage = computed(() => {
        const stage = ['DRAFT', 'APPROVAL', 'PUBLISHED']
        return stage.map(value => ({value, title: t(`stageType.${value}`)}))
    })

    const hotelId = computed(() => supplierStore.hotelInfo?.hotelId)

    const isLoadingChannelManagersConfig = ref(false)
    const channelManagersConfigItems = ref([])

    watch(hotelId, async newHotelId => {
        if (!newHotelId) return
        isLoadingChannelManagersConfig.value = true
        try {
            const data = await app.$api.channelManagersConfig.getForHotel(newHotelId)
            channelManagersConfigItems.value = data || []
        } catch (e) {
            console.error('Failed to load hotel channel managers config:', e)
        } finally {
            isLoadingChannelManagersConfig.value = false
        }
    }, {immediate: true})

    const saveChannelManagersConfig = async targetHotelId => {
        try {
            await app.$api.channelManagersConfig.updateForHotel(targetHotelId, channelManagersConfigItems.value)
        } catch (error) {
            console.error('Failed to save hotel channel managers config:', error)
            const errorMessage = error?.msg || error?.errors?.[0]?.message || t('error_message.save_error')
            app.$toast.error(errorMessage)
        }
    }
</script>

<style scoped>
    .custom-disabled {
        /* Override Vuetify's disabled styles */
        opacity: 1 !important;
        cursor: not-allowed !important;
        /* Ensure pointer events work for tooltip */
        pointer-events: auto !important;
    }

    .custom-disabled :deep(.v-input) {
        /* Override Vuetify's v-input--disabled pointer-events */
        pointer-events: auto !important;
    }

    .custom-disabled :deep(.v-field) {
        /* Custom disabled appearance - visible but clearly disabled */
        background-color: rgba(0, 0, 0, 0.04) !important;
        color: rgba(0, 0, 0, 0.6) !important;
        opacity: 1 !important;
    }

    .custom-disabled :deep(.v-field__input) {
        /* Disabled input text - visible */
        color: rgba(0, 0, 0, 0.6) !important;
        cursor: not-allowed !important;
    }

    .custom-disabled :deep(.v-field__append-inner) {
        /* Disabled dropdown arrow - visible */
        color: rgba(0, 0, 0, 0.6) !important;
        cursor: not-allowed !important;
    }

    .custom-disabled :deep(.v-field__outline) {
        /* Disabled border - visible */
        border-color: rgba(0, 0, 0, 0.3) !important;
    }

    .custom-disabled :deep(.v-label) {
        /* Disabled label - visible */
        color: rgba(0, 0, 0, 0.6) !important;
    }

    /* Ensure all elements allow pointer events for tooltip */
    .custom-disabled :deep(.v-field__input),
    .custom-disabled :deep(.v-field__append-inner),
    .custom-disabled :deep(.v-field__prepend-inner),
    .custom-disabled :deep(.v-field__overlay) {
        cursor: not-allowed !important;
        pointer-events: auto !important;
    }

    .disabled-supplier-text-field :deep(input) {
        user-select: none;
        -webkit-user-select: none; /* For Safari */
        -moz-user-select: none; /* For Firefox */
        -ms-user-select: none; /* For Internet Explorer/Edge */
    }
</style>
